import { useParams } from 'next/navigation';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AppState } from '@/store';
import { BrowserLanguage } from '@/store/browserLanguage/types';
import { Locale } from '@/store/locale/types';
import { getCalmLanguage } from '@/utils/language';

export function useLocaleState(): Locale | null {
	return useSelector((state: AppState) => state.locale);
}
export function useBrowserLanguageState(): BrowserLanguage | null {
	return useSelector((state: AppState) => state.browserLanguage);
}

export function useLocale(): {
	localeState: Locale | null;
	browserLanguageState: BrowserLanguage | null;
	routerLocale: string | null;
	calmLanguage: Locale;
} {
	const localeState = useLocaleState();
	const browserLanguageState = useBrowserLanguageState();
	const params = useParams();

	const browserCalmLanguage = useMemo(
		() => (browserLanguageState ? getCalmLanguage(browserLanguageState) : undefined),
		[browserLanguageState],
	);
	const routerCalmLanguage = useMemo(
		() => (typeof params?.locale === 'string' ? getCalmLanguage(params?.locale) : undefined),
		[params?.locale],
	);

	return useMemo(() => {
		return {
			localeState: localeState,
			browserLanguageState: browserLanguageState,
			routerLocale: typeof params?.locale === 'string' ? params.locale : null,
			calmLanguage: localeState ?? routerCalmLanguage ?? browserCalmLanguage ?? 'en',
		};
	}, [localeState, browserLanguageState, browserCalmLanguage, routerCalmLanguage, params?.locale]);
}
